import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { dashboardService } from '../services';
import { PerformanceResponse, DashboardPeriodValue } from '../types';
import {
  DASHBOARD_QUERY_STALE_TIME_MS,
  DASHBOARD_QUERY_GC_TIME_MS,
  DASHBOARD_QUERY_REFETCH_ON_WINDOW_FOCUS,
} from './dashboardQueryPolicy';

/**
 * Hook para buscar dados de performance
 *
 * @param period - Valor do seletor de período (F81 — shared PeriodPicker):
 *                 `{ mode: 'week' | 'month' | 'year' }` ou
 *                 `{ mode: 'custom', start, end }` (yyyy-mm-dd)
 * @param channelId - Canal de atendimento; ausente = todos os canais
 *
 * @example
 * const { data: performance, isLoading } = usePerformanceData({ mode: 'week' });
 */
export const usePerformanceData = (
  period: DashboardPeriodValue = { mode: 'month' },
  channelId?: string
): UseQueryResult<PerformanceResponse, Error> => {
  const isCustom = period.mode === 'custom';
  const customReady = !isCustom || (!!period.start && !!period.end);

  return useQuery({
    queryKey: ['performance-data', period.mode, period.start, period.end, channelId ?? null],
    queryFn: () =>
      dashboardService.getPerformanceData(
        period.mode,
        undefined,
        isCustom && period.start && period.end
          ? { start: period.start, end: period.end }
          : undefined,
        channelId
      ),
    enabled: customReady,
    // Política compartilhada com useDashboardMetrics (D10): igualada ao hook
    // de métricas (o mais fresco dos dois) para o gráfico e o card baterem —
    // ver dashboardQueryPolicy.ts.
    staleTime: DASHBOARD_QUERY_STALE_TIME_MS,
    gcTime: DASHBOARD_QUERY_GC_TIME_MS,
    refetchOnWindowFocus: DASHBOARD_QUERY_REFETCH_ON_WINDOW_FOCUS,
    retry: 2,
  });
};
